import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const label = "block font-sans-ui text-[10px] tracking-mega text-ink/50 mb-2";
const input = "w-full bg-transparent border-b hairline py-3 font-sans-ui text-sm tracking-wide focus:border-ink focus:outline-none";

const Dot = ({ on, testid, text }) => (
  <div className="flex items-center gap-2">
    <span data-testid={testid} className={`w-2 h-2 rounded-full ${on ? "bg-emerald-500" : "bg-ink/25"}`} />
    <span className="font-sans-ui text-[11px] tracking-mega text-ink/60">{text}</span>
  </div>
);

const detailOf = (err, fallback) => {
  const d = err.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

const TokenField = ({ meta, capiToken, setCapiToken, clearToken, setClearToken }) => (
  <div>
    <label className={label}>ACCESS TOKEN CONVERSIONS API</label>
    {meta.capi_configured && !clearToken && (
      <div className="flex items-center justify-between gap-4 bg-linen px-4 py-3 mb-3">
        <span data-testid="capi-token-masked" className="font-sans-ui text-sm tracking-widest text-ink/70">{meta.capi_token_masked}</span>
        <button type="button" data-testid="capi-token-clear" onClick={() => setClearToken(true)} className="font-sans-ui text-[10px] tracking-mega text-ink/50 hover:text-destructive transition-colors">RIMUOVI</button>
      </div>
    )}
    {clearToken && <p data-testid="capi-token-clear-notice" className="font-sans-ui text-xs text-destructive mb-3">Il token verrà rimosso al salvataggio.</p>}
    <input
      data-testid="capi-token-input"
      type="password"
      autoComplete="off"
      value={capiToken}
      onChange={(e) => { setCapiToken(e.target.value); setClearToken(false); }}
      placeholder={meta.capi_configured ? "Incolla un nuovo token per sostituirlo" : "EAAG..."}
      className={input}
    />
    <p className="mt-2 font-sans-ui text-[11px] text-ink/45 leading-relaxed">
      Events Manager → Impostazioni → Conversions API → "Genera token di accesso". Il token è cifrato a riposo, usato solo dal server e mai inviato al browser.
    </p>
  </div>
);

export const TrackingSettingsForm = ({ token, meta, onSaved }) => {
  const [pixelId, setPixelId] = useState("");
  const [capiToken, setCapiToken] = useState("");
  const [clearToken, setClearToken] = useState(false);
  const [testCode, setTestCode] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    setPixelId(meta.meta_pixel_id || "");
    setTestCode(meta.capi_test_event_code || "");
    setCapiToken("");
    setClearToken(false);
  }, [meta]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    const body = { meta_pixel_id: pixelId, capi_test_event_code: testCode };
    if (clearToken) body.capi_access_token = "";
    else if (capiToken.trim()) body.capi_access_token = capiToken.trim();
    try {
      const { data } = await axios.put(`${API}/admin/settings`, body, { headers: { Authorization: `Bearer ${token}` } });
      onSaved(data);
      setMsg({ ok: true, text: data.meta_pixel_id ? "Impostazioni salvate. Il tracking è operativo." : "Impostazioni salvate. Senza Pixel ID nessun evento verrà inviato." });
    } catch (err) {
      setMsg({ ok: false, text: detailOf(err, "Salvataggio non riuscito.") });
    } finally {
      setSaving(false);
    }
  };

  const pixelActive = !!meta.meta_pixel_id;

  return (
    <form onSubmit={save} className="bg-white border hairline p-8 space-y-8">
      <div>
        <p className="font-sans-ui text-[10px] tracking-mega text-ink/40 mb-3">TRACKING / META PIXEL</p>
        <h2 className="font-display text-3xl text-ink mb-2">Meta Pixel & Conversions API</h2>
        <p className="font-sans-ui text-[13px] text-ink/60 leading-relaxed">
          Nessuna modifica al codice: la landing carica il Pixel a runtime e il server invia gli eventi CAPI con il token qui salvato.
        </p>
      </div>

      <div className="flex flex-wrap gap-6">
        <Dot on={pixelActive} testid="pixel-status" text={pixelActive ? "PIXEL ATTIVO" : "PIXEL NON CONFIGURATO"} />
        <Dot on={!!meta.capi_configured} testid="capi-status" text={meta.capi_configured ? "CAPI ATTIVA" : "CAPI NON CONFIGURATA"} />
      </div>

      <div>
        <label className={label}>PIXEL ID</label>
        <input data-testid="pixel-id-input" value={pixelId} onChange={(e) => setPixelId(e.target.value.replace(/\D/g, ""))} inputMode="numeric" placeholder="es. 123456789012345" className={input} />
      </div>

      <TokenField meta={meta} capiToken={capiToken} setCapiToken={setCapiToken} clearToken={clearToken} setClearToken={setClearToken} />

      <div>
        <label className={label}>TEST EVENT CODE (OPZIONALE)</label>
        <input data-testid="capi-test-code-input" value={testCode} onChange={(e) => setTestCode(e.target.value.toUpperCase())} placeholder="TEST12345" className={input} />
        <p className="mt-2 font-sans-ui text-[11px] text-ink/45 leading-relaxed">
          Da Events Manager → Test Events. Lascialo vuoto in produzione: se compilato, tutti i Lead server-side vengono marcati come test.
        </p>
      </div>

      {msg && <p data-testid="pixel-save-message" className={`font-sans-ui text-sm ${msg.ok ? "text-emerald-700" : "text-destructive"}`}>{msg.text}</p>}

      <div className="flex flex-wrap gap-3">
        <button data-testid="pixel-save-button" type="submit" disabled={saving} className="bg-ink text-ivory px-6 py-3 text-[10px] tracking-mega font-semibold hover:bg-charcoal transition-colors disabled:opacity-50">
          {saving ? "SALVATAGGIO..." : "SALVA IMPOSTAZIONI"}
        </button>
        {pixelActive && (
          <button data-testid="pixel-remove-button" type="button" onClick={() => setPixelId("")} className="border hairline text-ink/60 px-6 py-3 text-[10px] tracking-mega hover:text-ink transition-colors">
            SVUOTA PIXEL
          </button>
        )}
      </div>
      {meta.updated_at && (
        <p className="font-sans-ui text-[11px] text-ink/40">Ultimo aggiornamento: {meta.updated_at.slice(0, 16).replace("T", " ")} · {meta.updated_by}</p>
      )}
    </form>
  );
};

export const CapiTestCard = ({ token, enabled }) => {
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState(null);

  const sendTest = async () => {
    setTesting(true);
    setMsg(null);
    try {
      const { data } = await axios.post(`${API}/admin/settings/capi-test`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setMsg({ ok: true, text: `Meta ha ricevuto ${data.events_received} evento. Controlla Events Manager → Test Events (codice ${data.test_event_code}).` });
    } catch (err) {
      setMsg({ ok: false, text: detailOf(err, "Invio di test non riuscito.") });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="bg-linen p-6">
      <p className="font-sans-ui text-[10px] tracking-mega text-ink/45 mb-3">VERIFICA CONVERSIONS API</p>
      <p className="font-sans-ui text-[13px] text-ink/65 leading-relaxed mb-4">
        Invia un evento Lead di prova ai server Meta con il token salvato. Comparirà in Events Manager → Test Events.
      </p>
      <button data-testid="capi-test-button" type="button" onClick={sendTest} disabled={testing || !enabled} className="bg-ink text-ivory px-6 py-3 text-[10px] tracking-mega font-semibold hover:bg-charcoal transition-colors disabled:opacity-40">
        {testing ? "INVIO..." : "INVIA EVENTO DI TEST"}
      </button>
      {msg && <p data-testid="capi-test-message" className={`mt-4 font-sans-ui text-sm ${msg.ok ? "text-emerald-700" : "text-destructive"}`}>{msg.text}</p>}
    </div>
  );
};
